import * as t from "io-ts";
import { NumberFromString } from "io-ts-types/lib/NumberFromString";

import { StreamValue } from "../../models/streamfield-blocks";
import { check, codecFromEnum } from "../../models/utils";

export type GridVariant = "mattress" | "pillows" | "streamfield";

export enum GridFilterSelectionMode {
    SELECT_ONE = "one",
    SELECT_MANY = "many",
}

export enum PriceComparator {
    LT = "LT",
    LTE = "LTE",
    GT = "GT",
    GTE = "GTE",
}

export enum SortDirection {
    ASC = "ASC",
    DESC = "DESC",
}

const GridFilterSelectionModeCodec = codecFromEnum(
    "GridFilterSelectionMode",
    GridFilterSelectionMode,
);
const PriceComparatorCodec = codecFromEnum("PriceComparator", PriceComparator);
const SortDirectionCodec = codecFromEnum("SortDirection", SortDirection);

const SingleBoundPriceRange = StreamValue(
    "single_bound_price_range",
    t.readonly(
        t.type({
            id: t.string,
            comparator: PriceComparatorCodec,
            amount: NumberFromString,
        }),
    ),
);
export type ISingleBoundPriceRange = t.TypeOf<typeof SingleBoundPriceRange>;

const DualBoundPriceRange = StreamValue(
    "dual_bound_price_range",
    t.readonly(
        t.type({
            id: t.string,
            lower_bound: t.readonly(
                t.type({
                    comparator: PriceComparatorCodec,
                    amount: NumberFromString,
                }),
            ),
            upper_bound: t.readonly(
                t.type({
                    comparator: PriceComparatorCodec,
                    amount: NumberFromString,
                }),
            ),
        }),
    ),
);
export type IDualBoundPriceRange = t.TypeOf<typeof DualBoundPriceRange>;

const PriceRange = t.union([SingleBoundPriceRange, DualBoundPriceRange]);
export type IPriceRange = t.TypeOf<typeof PriceRange>;

const BooleanAttribute = t.readonly(
    t.type({
        attribute: t.string,
        label: t.string,
    }),
);
export type IBooleanAttribute = t.TypeOf<typeof BooleanAttribute>;

/**
 * What every filter carries, whatever kind it is.
 *
 * Mirrors `_GridFilter` on the server, which is a base `StructBlock` the six
 * filter types inherit from -- which is why a field added there is one edit
 * and was six here. Shared once so the next one is one edit on both sides:
 * `t.type` is not exact, so a field left off a single codec still arrives at
 * runtime and merely goes missing from the decoded type, which surfaces as a
 * filter behaving oddly rather than as anything a compiler catches.
 */
const GridFilterBaseFields = t.readonly(
    t.type({
        // A string on every type, including `_AttributeValueGridFilter`,
        // which overrides it with a chooser: the chosen attribute serializes
        // as its code.
        filter_id: t.string,
        label: t.string,
        selection_mode: GridFilterSelectionModeCodec,
        show_in_sidebar: t.union([t.boolean, t.undefined]),
    }),
);

const AttributeValueGridFilter = StreamValue(
    "attribute_value_grid_filter",
    GridFilterBaseFields,
);

const ProductClassGridFilter = StreamValue(
    "product_class_grid_filter",
    GridFilterBaseFields,
);

const PriceRangeGridFilter = StreamValue(
    "price_range_grid_filter",
    t.intersection([
        GridFilterBaseFields,
        t.readonly(
            t.type({
                ranges: t.readonlyArray(PriceRange),
            }),
        ),
    ]),
);

const BooleanAttributeGridFilter = StreamValue(
    "boolean_attribute_grid_filter",
    t.intersection([
        GridFilterBaseFields,
        t.readonly(
            t.type({
                attributes: t.readonlyArray(BooleanAttribute),
            }),
        ),
    ]),
);

const RatingRangeGridFilter = StreamValue(
    "rating_range_grid_filter",
    t.intersection([
        GridFilterBaseFields,
        t.readonly(
            t.type({
                // Plain numbers, not `NumberFromString` like the price
                // amounts: those come off a `DecimalBlock`, which
                // serializes as a string.
                ratings: t.readonlyArray(t.number),
            }),
        ),
    ]),
);

const DiscountGridFilter = StreamValue(
    "discount_grid_filter",
    t.intersection([
        GridFilterBaseFields,
        t.readonly(
            t.type({
                // Optional in the CMS; blank means "reuse the group
                // label".
                option_label: t.string,
            }),
        ),
    ]),
);

const AttributeValueGridSorter = StreamValue(
    "attribute_value_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
            attribute: t.string,
        }),
    ),
);

const PriceGridSorter = StreamValue(
    "price_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const PillowsPriceGridSorter = StreamValue(
    "pillows_price_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const RatingGridSorter = StreamValue(
    "rating_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const RatingNumberGridSorter = StreamValue(
    "rating_number_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const DateCreatedGridSorter = StreamValue(
    "date_created_grid_sorter",
    t.readonly(
        t.type({
            id: t.string,
            label: t.string,
            direction: SortDirectionCodec,
        }),
    ),
);

const GridFilter = t.union([
    AttributeValueGridFilter,
    ProductClassGridFilter,
    PriceRangeGridFilter,
    BooleanAttributeGridFilter,
    RatingRangeGridFilter,
    DiscountGridFilter,
]);

const GridSorter = t.union([
    AttributeValueGridSorter,
    PriceGridSorter,
    PillowsPriceGridSorter,
    RatingGridSorter,
    RatingNumberGridSorter,
    DateCreatedGridSorter,
]);

export const GridFilters = t.readonlyArray(GridFilter);
export const GridSorters = t.readonlyArray(GridSorter);

export type IGridFilters = t.TypeOf<typeof GridFilters>;

/**
 * Decode a grid's `filters_data` payload.
 *
 * The one decoder for that string. `StreamFieldProductGrid` builds the live
 * filters from it and callers that only want to read the configuration --
 * a tile wanting the wording an editor gave an option, say -- go through here
 * rather than reaching for the codec, so a schema change lands in one place
 * instead of once per reader.
 *
 * Throws on a malformed payload, as the grid's own decode does; a caller that
 * swallows it would only be deferring the same failure by one component.
 */
export const parseGridFilters = (filtersJSON: string): IGridFilters =>
    check(GridFilters.decode(JSON.parse(filtersJSON)));

export type IGridSorters = t.TypeOf<typeof GridSorters>;

/** Decode a grid's `sorters_data` payload. See :func:`parseGridFilters`. */
export const parseGridSorters = (sortersJSON: string): IGridSorters =>
    check(GridSorters.decode(JSON.parse(sortersJSON)));

/**
 * The options of one Boolean Attribute filter, in the order an editor put
 * them in, or nothing when the grid has no such filter under that id.
 *
 * Exists so a caller that wants an option's authored label does not have to
 * know how filter configs are shaped -- the alternative is a `type` switch in
 * every consumer, which is the StreamField schema leaking out of this package.
 *
 * Paired with `_GridFilter.list_boolean_attributes` on the Python side, which
 * is what decides server-side whether a filter carries attribute/label pairs.
 * The trait is not serialized into `filters_data`, so this end can only
 * discriminate on the block type name: a second filter type that implements
 * that hook has to be added to the check below too, or the server will accept
 * a grid this returns nothing for.
 */
export const getBooleanAttributeOptions = (
    filtersJSON: string,
    filterID: string,
): readonly IBooleanAttribute[] => {
    for (const filter of parseGridFilters(filtersJSON)) {
        if (
            filter.type === "boolean_attribute_grid_filter" &&
            filter.value.filter_id === filterID
        ) {
            return filter.value.attributes;
        }
    }
    return [];
};

export type IProductCardType = "grid-card" | "sub-category-card-grid";

/**
 * Where a grid puts its sort control.
 *
 * `"top"` is the shared dropdown above the tiles, which is what every grid
 * had before there was a choice. `"sidebar"` renders the same sorters as an
 * accordion over the filters instead, which is what the 1-800 design asks
 * for -- one column where a shopper narrows and orders the same list.
 *
 * Either way it is the one control: the placement moves it rather than
 * duplicating it, so a grid cannot offer two sorts that disagree.
 */
export type GridSortPlacement = "top" | "sidebar";

export type IGridFilterOptions = Readonly<{
    filter_id: string;
    label: string;
    selection_mode?: GridFilterSelectionMode;
    /**
     * Whether the sidebar draws an accordion for this filter.
     *
     * Optional because grids authored before the field existed do not carry
     * it, and absent means shown -- which is what every one of them was.
     */
    show_in_sidebar?: boolean;
}>;

export type IGridSorterConfigOptions = Readonly<{
    id: string;
    label: string;
    direction: SortDirection;
}>;

export type IFilterOption = Readonly<{
    id: string;
    label: string;
    /**
     * Optional visual treatment for the checkbox label, as data.
     *
     * Deliberately a descriptor rather than a `ReactNode`: this module and
     * `filters.ts` stay presentation-free, so which component draws it and
     * how it is styled remain decisions of the view layer -- the same split
     * the reviews app uses, where `ISearchFacetOption` carries an id and
     * `RatingFilterOption` owns the `RatingGraphic` props. It also keeps
     * `listFilterOptions()` cheap: it is called two or three times per render
     * and would otherwise allocate a throwaway element tree each time.
     *
     * `label` stays a plain string regardless -- it is the `<option>` text in
     * the select-one dropdown and is collected into a `string[]` for the
     * applied-filter summary, neither of which can take a node.
     */
    graphic?: Readonly<{ kind: "rating"; rating: number }>;
}>;
